import React, { useState } from 'react';
import { X, Loader2 } from 'lucide-react';
import { supabase } from '../../src/lib/supabase';
import { useAuth } from '../../src/contexts/AuthContext';
import type { Projeto, Profile } from '../../types';
import TagsInput from './TagsInput';

interface ProjetoFormModalProps {
  /** null = criação. */
  projeto: Projeto | null;
  users: Profile[];
  onClose: () => void;
  onSaved: (projeto: Projeto) => void;
}

const ProjetoFormModal: React.FC<ProjetoFormModalProps> = ({ projeto, users, onClose, onSaved }) => {
  const { user } = useAuth();
  const [nome, setNome] = useState(projeto?.nome || '');
  const [descricao, setDescricao] = useState(projeto?.descricao || '');
  const [responsavelId, setResponsavelId] = useState(projeto?.responsavel_id || '');
  const [prazo, setPrazo] = useState(projeto?.prazo?.slice(0, 10) || '');
  const [tags, setTags] = useState<string[]>(projeto?.tags || []);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  const salvar = async () => {
    if (!nome.trim() || salvando) return;
    setSalvando(true);
    setErro(null);
    const dados = {
      nome: nome.trim(),
      descricao: descricao.trim() || null,
      responsavel_id: responsavelId || null,
      prazo: prazo || null,
      tags,
      updated_at: new Date().toISOString(),
    };
    const query = projeto
      ? supabase.from('projetos').update(dados).eq('id', projeto.id)
      : supabase.from('projetos').insert([{ ...dados, criado_por: user?.id }]);
    const { data, error } = await query
      .select('*, responsavel:profiles!projetos_responsavel_id_fkey(id, full_name, avatar_url)')
      .single();
    setSalvando(false);
    if (error) { setErro(error.message); return; }
    onSaved(data as Projeto);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-[100] bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white dark:bg-gray-800 rounded-xl w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="font-bold text-gray-800 dark:text-white">{projeto ? 'Editar projeto' : 'Novo projeto'}</h2>
          <button onClick={onClose} aria-label="Fechar"><X size={18} className="text-gray-400" /></button>
        </div>

        <div className="p-5 space-y-4">
          <div>
            <label className="block text-xs font-semibold text-gray-600 dark:text-gray-300 mb-1">Nome</label>
            <input value={nome} onChange={e => setNome(e.target.value)} autoFocus
              className="w-full px-3 py-2 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg text-sm dark:text-slate-200" />
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-600 dark:text-gray-300 mb-1">Descrição</label>
            <textarea value={descricao} onChange={e => setDescricao(e.target.value)} rows={4}
              className="w-full px-3 py-2 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg text-sm dark:text-slate-200 resize-y" />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              {/* Só o gestor: os demais responsáveis entram sozinhos, pelas tarefas vinculadas. */}
              <label className="block text-xs font-semibold text-gray-600 dark:text-gray-300 mb-1">Responsável pelo projeto</label>
              <select value={responsavelId} onChange={e => setResponsavelId(e.target.value)}
                className="w-full px-3 py-2 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg text-sm dark:text-slate-200">
                <option value="">Sem responsável</option>
                {users.filter(u => u.status !== 'inactive').map(u => (
                  <option key={u.id} value={u.id}>{u.full_name || u.email}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 dark:text-gray-300 mb-1">Prazo</label>
              <input type="date" value={prazo} onChange={e => setPrazo(e.target.value)}
                className="w-full px-3 py-2 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg text-sm dark:text-slate-200" />
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-600 dark:text-gray-300 mb-1">Tags</label>
            <TagsInput tags={tags} onChange={setTags} />
          </div>

          {erro && <p className="text-xs text-rose-600 dark:text-rose-400">{erro}</p>}
        </div>

        <div className="flex justify-end gap-2 px-5 py-4 border-t border-gray-200 dark:border-gray-700">
          <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600 dark:text-gray-300">Cancelar</button>
          <button onClick={salvar} disabled={!nome.trim() || salvando}
            className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-white text-sm font-semibold rounded-lg disabled:opacity-50">
            {salvando && <Loader2 size={14} className="animate-spin" />}
            Salvar
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProjetoFormModal;
